import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { MediaContext } from '../commonV3/context';
import { checkIsV3 } from '../../utils/getAppVersion';
import { useDispatch, useSelector } from 'react-redux';
import { updateHotspotDataInterior, setToggleInteriorHotspot, setToggleHotspotView, setInteriorHotspotIndex } from '../../Redux/Reducers/View360Reducer/View360ActionController';
import InteriorIframe from '../View360/InteriorIframe';
import HotspotToggleHandler from '../View360/HotspotToggleHandler/HotspotToggleHandler';
import MobileToggleHandlerV3 from '../View360/HotspotToggleHandler/MobileToggleHandlerV3';
import MobileViewSliderV3 from '../View360/MobileViewSliderV3';
import { IMAGE_COMPRESSOR } from '../../utils/imageCompressor';

const Interior360 = ({ 
  selectedTemplate,
  state,
  isMobileView,
  interiorTimeSpent,
  isCommonIframeT1T2 = true,
  automatedImageSpin = true,
  onHotspotIndexChange,
  isMobileFullScreen = false
}) => {
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);
  const hotspot_val = queryParams.get('intHotspot')
    ? JSON.parse(queryParams.get('intHotspot'))
    : (queryParams.get('hotspot') ? JSON.parse(queryParams.get('hotspot')) : true);
  const hotspotDataInterior = useSelector(state => state.view360Reducer.hotspotDataInterior);
  const toggleInteriorHotspots = useSelector(state => state.view360Reducer.toggleInteriorHotspots);
  const toggleHotspotView = useSelector(state => state.view360Reducer.toggleHotspotView);
  const [isLoading, setIsLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [automatedSpin, setAutomatedSpin] = useState(true);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [modalList, setModalList] = useState(new Map());
  const [goToSlide, setGoToSlide] = useState(0);
  const [isAnyFocusImage, setIsAnyFocusImage] = useState(false);
  const isV3 = checkIsV3();
  const { enterpriseId } = useContext(MediaContext);
  const [isSpanish, setIsSpanish] = useState(false);
  // Check if there are any hotspots
  const hasHotspots = useMemo(() => {
    return (state?.interiorData && state.interiorData.length > 0) || 
           (hotspotDataInterior && Object.keys(hotspotDataInterior).length > 0);
  }, [state?.interiorData, hotspotDataInterior]);

  // Set loading to false when component mounts
  useEffect(() => {
    setIsLoading(false);
    setIsMounted(true);
  }, []);

  const spanishEnterprises = useMemo(() => ['2d410b1b9','204fffd0d'], []); // TODO: get from backend
  const featureDescBypassEnterprises = useMemo(() => ['858b283d5', 'a45469aae'], []);
  const isSpanishEnterpriseFn = useCallback((enterpriseId) => {
      return spanishEnterprises.includes(enterpriseId);
  }, [spanishEnterprises]);
  const isFeatureDescBypassEnterpriseFn = useCallback((enterpriseId) => {
      return featureDescBypassEnterprises.includes(enterpriseId);
  }, [featureDescBypassEnterprises]);
  const [bypassFeatureDesc, setBypassFeatureDesc] = useState(false);

  useEffect(() => {
    if(isSpanishEnterpriseFn(enterpriseId)){
      setIsSpanish(true);
    }
    if(isFeatureDescBypassEnterpriseFn(enterpriseId)){
      setBypassFeatureDesc(true);
    }
  }, [enterpriseId, isSpanishEnterpriseFn, isFeatureDescBypassEnterpriseFn])

  // Add useEffect for automatedSpin
  useEffect(() => {
    setAutomatedSpin(!toggleInteriorHotspots);
  }, [toggleInteriorHotspots]);

  // Get image URL from state
  const frame = state?.interior360Url?.data?.[0];
  const imageUrl = frame?.output_image_hres_url || frame?.input_image_hres_url;

  // Use frame.image_id for hotspotImageId
  const hotspotImageId = useMemo(() => {
    return frame?.image_id || '';
  }, [frame?.image_id]);

  // Build modalList from hotspot data
  useEffect(() => {
    const buildModalList = () => {
      const newModalList = new Map();
      let hasAnyFocus = false;
      
      // Get hotspot data from state.interiorData or hotspotDataInterior
      const hotspotData = state?.interiorData || hotspotDataInterior || [];
      
      if (Array.isArray(hotspotData)) {
        hotspotData.forEach((hotSpotObj) => {
          if (hotSpotObj && hotSpotObj.hotspot_id) {
            // Check if hotspot has focus image
            if (hotSpotObj.focus_image_url) {
              hasAnyFocus = true;
            }
            
            // Build modal list entry similar to View360
            if (isV3 && !isSpanish && !bypassFeatureDesc) {
              if (hotSpotObj.feature && hotSpotObj.feature.trim() !== '') {
                newModalList.set(hotSpotObj.hotspot_id, {
                  title: hotSpotObj.type || '',
                  heading: hotSpotObj.reasons?.[0] || '',
                  desc: hotSpotObj.feature || '',
                  url: hotSpotObj.hotspot_icon || '',
                  closeUpURL: hotSpotObj?.focus_image_url ? IMAGE_COMPRESSOR({ url: hotSpotObj?.focus_image_url }) : '',
                  image_id: hotspotImageId
                });
              }
            } else {
              newModalList.set(hotSpotObj.hotspot_id, {
                title: hotSpotObj.type || '',
                heading: hotSpotObj.reasons?.[0] || '',
                desc: hotSpotObj.reasons?.[0] || '',
                url: hotSpotObj.hotspot_icon || '',
                closeUpURL: hotSpotObj?.focus_image_url ? IMAGE_COMPRESSOR({ url: hotSpotObj?.focus_image_url }) : '',
                image_id: hotspotImageId
              });
            }
          }
        });
      }
      
      setModalList(newModalList);
      setIsAnyFocusImage(hasAnyFocus);
    };

    buildModalList();
  }, [state?.interiorData, hotspotDataInterior, hotspotImageId, isV3, isSpanish, bypassFeatureDesc]);

  // Update Redux store when hotspot data changes
  useEffect(() => {
    if (hotspotDataInterior) {
      dispatch(updateHotspotDataInterior(hotspotDataInterior));
    }
  }, [hotspotDataInterior, dispatch]);


  // Toggle hotspot visibility (Redux only)
  const handleHotSpotViewToggle = useCallback((e) => {
    e.stopPropagation();
    dispatch(setToggleInteriorHotspot(!toggleInteriorHotspots));
    dispatch(setToggleHotspotView(!toggleHotspotView));
    dispatch(setInteriorHotspotIndex(-1));
  }, [dispatch, toggleInteriorHotspots, toggleHotspotView]);

  // Handle hotspot selection to show modal
  const handleHotspotClick = useCallback((hotspotId, index) => {
    const modalListKeys = [...modalList.keys()];
    const foundIndex = modalListKeys.findIndex(key => key === hotspotId);
    
    if (foundIndex >= 0) {
      setActiveHotspot(true);
      setGoToSlide(foundIndex);
    }
  }, [modalList]);

  // Navigation functions for MobileViewSliderV3
  const focusPoint = useCallback((index) => {
    if (index === goToSlide) return;
    
    const modalListKeys = [...modalList.keys()];
    if (modalListKeys[index]) {
      setGoToSlide(index);
    }
  }, [modalList, goToSlide]);

  const increment = useCallback(() => {
    const modalListKeys = [...modalList.keys()];
    const newIndex = goToSlide < modalListKeys.length - 1 ? goToSlide + 1 : 0;
    focusPoint(newIndex);
  }, [modalList, goToSlide, focusPoint]);

  const decrement = useCallback(() => {
    const modalListKeys = [...modalList.keys()];
    const newIndex = goToSlide > 0 ? goToSlide - 1 : modalListKeys.length - 1;
    focusPoint(newIndex);
  }, [modalList, goToSlide, focusPoint]);

  const hideModal = useCallback(() => {
    setActiveHotspot(null);
  }, []);

  // Early return if no valid image URL
  if (!imageUrl) {
    console.warn('No valid image URL available');
    return null;
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}> 
      {isLoading && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.1)',
          zIndex: 1
        }}>
          Loading...
        </div>
      )}
      {isMounted && (<InteriorIframe
        automatedImageSpin={automatedSpin}
        state={{
          interior360Url:[{
              ...frame,
              output_image_hres_url: imageUrl,
              input_image_hres_url: imageUrl,
            }]
          ,
          selectedUrl: {
            image_id: frame?.image_id || '',
            url: imageUrl
          },
          hotspotImageId: hotspotImageId,
          toggleInteriorHotspots: toggleInteriorHotspots
        }}
        spanishEnterprise={isSpanish}
        isMobileView={isMobileView}
        hotspots={state?.interiorData}
        ToggleInteriorHotspots={toggleInteriorHotspots}
        hotspotDataInterior={hotspotDataInterior}
        hotspotImageId={hotspotImageId}
        interiorTimeSpent={interiorTimeSpent}
        selectedTemplate={selectedTemplate}
        autoRotateInactivityDelay={3000}
        isCommonIframeT1T2={isCommonIframeT1T2}
        onHotspotIndexChange={onHotspotIndexChange}
        isMobileFullScreen={isMobileFullScreen}
        onHotspotClick={handleHotspotClick}
      />)}
      {/* Only show toggle button if ALL conditions are met */}
      { hasHotspots && !isMobileView && selectedTemplate !== 'mobile' && hotspot_val && !isMobileFullScreen && (
  <div
    className={`absolute bottom-[8px] md:bottom-[15px] right-[8px] md:right-[45px] ${isV3 ? 'bottom-[5px] md:right-[35px]' : ''} z-[1111111]`}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
  >
    <HotspotToggleHandler
      handleHotSpotViewToggle={handleHotSpotViewToggle}
      toggleHotspotView={toggleInteriorHotspots}
      isSpanish={isSpanish}
    />
  </div>
)}
  { isV3 && hasHotspots && hotspot_val && (isMobileFullScreen || selectedTemplate === 'mobile') && (
 
  <div
    className={`absolute bottom-[11.5px] md:bottom-[15px] right-[7px] md:right-[45px] ${isV3 ? 'bottom-[5px] md:right-[35px]' : ''} z-[1111111]`}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
  >
    <MobileToggleHandlerV3
      handleHotSpotViewToggle={handleHotSpotViewToggle}
      toggleHotspotView={toggleInteriorHotspots}
      isSpanish={isSpanish}
      isInterior={true}
    />
  </div>
)}

      {/* MobileViewSliderV3 for Interior360 hotspots */}
      {(activeHotspot && isAnyFocusImage && isV3 && isMobileView && selectedTemplate === 'mobile') && (
        <MobileViewSliderV3 
          modalList={modalList}
          hideModal={hideModal}
          goToSlide={goToSlide}
          updateFocusPoint={focusPoint}
          increment={increment}
          decrement={decrement}
          type="interior360"
        />
      )}
    </div>
  );
};

export default React.memo(Interior360); 